import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Charts
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

export default function DashboardRRHH() {
  const [stats, setStats] = useState({
    totalEmpleados: 0,
    empleadosActivos: 0,
    nuevosEmpleados: 0,
    empleadosInactivos: 0,
    nominaDelMes: 0,
    promedioSalario: 0,
    asistenciaPromedio: 0,
    vacacionesPendientes: 0,
  });

  const [deptData, setDeptData] = useState({
    labels: [],
    datasets: [{ label: "Empleados por Departamento", data: [], backgroundColor: [] }],
  });

  const [asistenciaData, setAsistenciaData] = useState({
    labels: [],
    datasets: [],
  });

  const [nominaData, setNominaData] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.RRHH.DASHBOARD);
        setStats(response.data.stats);
        setDeptData(response.data.deptData);
        setAsistenciaData(response.data.asistenciaData);
        setNominaData(response.data.nominaData);
      } catch (error) {
        console.error("Error al cargar dashboard RRHH:", error);
        // Fallback or error handling can go here
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  return (
    <MDBox>
      <Grid container spacing={3}>
        {/* KPIs principales */}
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="dark"
              icon="people"
              title="Total Empleados"
              count={stats.totalEmpleados}
              percentage={{
                color: "success",
                amount: "+5%",
                label: "vs mes anterior",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="person_add"
              title="Nuevos Empleados"
              count={stats.nuevosEmpleados}
              percentage={{
                color: "success",
                amount: "+12",
                label: "este mes",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="primary"
              icon="payments"
              title="Nómina del Mes"
              count={formatCurrency(stats.nominaDelMes)}
              percentage={{
                color: "success",
                amount: "+3.2%",
                label: "vs mes anterior",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="info"
              icon="trending_up"
              title="Asistencia Promedio"
              count={`${stats.asistenciaPromedio}%`}
              percentage={{
                color: "success",
                amount: "+1.2%",
                label: "vs mes anterior",
              }}
            />
          </MDBox>
        </Grid>

        {/* Gráficas */}
        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Distribución por Departamento
              </MDTypography>
              <MDBox height="300px">
                <Doughnut
                  data={deptData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                      },
                    },
                  }}
                />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Asistencias de la Semana
              </MDTypography>
              <MDBox height="300px">
                <Bar
                  data={asistenciaData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 150,
                      },
                    },
                  }}
                />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Evolución Nómina Mensual
              </MDTypography>
              <MDBox height="300px">
                <Bar
                  data={nominaData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function (value) {
                            return "$" + value.toLocaleString();
                          },
                        },
                      },
                    },
                  }}
                />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Indicadores adicionales */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Indicadores Clave de RRHH
              </MDTypography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox
                    textAlign="center"
                    p={2}
                    sx={{ bgcolor: "success.lighter", borderRadius: 2 }}
                  >
                    <MDTypography variant="caption" color="text">
                      Empleados Activos
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="success">
                      {stats.empleadosActivos}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox textAlign="center" p={2} sx={{ bgcolor: "info.lighter", borderRadius: 2 }}>
                    <MDTypography variant="caption" color="text">
                      Promedio Salarial
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="info">
                      {formatCurrency(stats.promedioSalario)}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox
                    textAlign="center"
                    p={2}
                    sx={{ bgcolor: "warning.lighter", borderRadius: 2 }}
                  >
                    <MDTypography variant="caption" color="text">
                      Vacaciones Pendientes
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="warning">
                      {stats.vacacionesPendientes}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox
                    textAlign="center"
                    p={2}
                    sx={{ bgcolor: "error.lighter", borderRadius: 2 }}
                  >
                    <MDTypography variant="caption" color="text">
                      Empleados Inactivos
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="error">
                      {stats.empleadosInactivos}
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>

              <MDBox mt={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <MDTypography variant="button" fontWeight="medium">
                        Tasa de Rotación
                      </MDTypography>
                      <MDTypography variant="button" color="success" fontWeight="bold">
                        4.8%
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <MDTypography variant="button" fontWeight="medium">
                        Tiempo Promedio de Contratación
                      </MDTypography>
                      <MDTypography variant="button" color="info" fontWeight="bold">
                        18 días
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <MDTypography variant="button" fontWeight="medium">
                        Satisfacción del Empleado
                      </MDTypography>
                      <MDTypography variant="button" color="success" fontWeight="bold">
                        8.7/10
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={1}>
                      <MDTypography variant="button" fontWeight="medium">
                        Cumplimiento de Capacitaciones
                      </MDTypography>
                      <MDTypography variant="button" color="warning" fontWeight="bold">
                        82%
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
